import React from "react";
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
    page: {
        backgroundColor: "#fff",
        padding: 30,
        fontSize: 12,
        fontFamily: "Helvetica",
    },
    header: {
        textAlign: "center",
        marginBottom: 20,
        borderBottomWidth: 1,
        borderBottomColor: "#e2e8f0",
        paddingBottom: 15,
    },
    title: {
        fontSize: 22,
        fontWeight: "bold",
        color: "#1e3a8a",
        marginBottom: 5,
    },
    subTitle: {
        fontSize: 14,
        color: "#475569",
        marginBottom: 10,
    },
    filterBox: {
        flexDirection: "row",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 10,
        backgroundColor: "#f8fafc",
        padding: 8,
        borderRadius: 4,
    },
    filterText: {
        fontSize: 10,
        color: "#334155",
    },
    filterBold: {
        fontWeight: "bold",
        color: "#0f172a",
    },
    table: {
        width: "100%",
        flexDirection: "column",
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "#cbd5e1",
        borderRadius: 4,
        marginTop: 15,
    },
    tableRow: {
        flexDirection: "row",
    },
    tableRowEven: {
        flexDirection: "row",
        backgroundColor: "#f8fafc",
    },
    tableColHeader: {
        width: "50%",
        backgroundColor: "#e2e8f0",
        padding: 10,
        borderBottomWidth: 1,
        borderBottomColor: "#cbd5e1",
    },
    tableCol: {
        width: "50%",
        padding: 10,
        borderBottomWidth: 1,
        borderBottomColor: "#e2e8f0",
    },
    tableCellHeader: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#0f172a",
    },
    tableCell: {
        fontSize: 11,
        color: "#334155",
    },
    tableCellBold: {
        fontSize: 11,
        fontWeight: "bold",
        color: "#0f172a",
    },
    footer: {
        position: "absolute",
        bottom: 30,
        left: 30,
        right: 30,
        textAlign: "center",
        fontSize: 10,
        color: "#64748b",
        borderTopWidth: 1,
        borderTopColor: "#e2e8f0",
        paddingTop: 10,
    },
});

interface DeputyReportPDFProps {
    summary: any;
    trendData: { name: string; total: number }[];
    selectedMonth: number;
    selectedYear: number;
    violationFilter: string;
    endorserFilter: string;
}

export const DeputyReportPDF: React.FC<DeputyReportPDFProps> = ({
    summary,
    trendData,
    selectedMonth,
    selectedYear,
    violationFilter,
    endorserFilter,
}) => {
    const monthName = selectedMonth === 0 
        ? "All Months"
        : new Date(0, selectedMonth - 1).toLocaleString("default", { month: "long", });

    const displayViolation = violationFilter === "ALL" ? "All" : violationFilter === "WITH" ? "With Violation" : "Without Violation";

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.title}>Deputy Dashboard Report</Text>
                    <Text style={styles.subTitle}>
                        {monthName} {selectedYear}
                    </Text>
                    
                    <View style={styles.filterBox}>
                        <Text style={styles.filterText}>
                            Violation Status: <Text style={styles.filterBold}>{displayViolation}</Text>
                        </Text>
                        <Text style={styles.filterText}> | </Text>
                        <Text style={styles.filterText}>
                            Endorser: <Text style={styles.filterBold}>{endorserFilter}</Text>
                        </Text>
                    </View>
                </View>

                {/* Table */}
                <View style={styles.table}>
                    <View style={styles.tableRow}>
                        <View style={styles.tableColHeader}>
                            <Text style={styles.tableCellHeader}>Category</Text>
                        </View>
                        <View style={styles.tableColHeader}>
                            <Text style={styles.tableCellHeader}>Report Count</Text>
                        </View>
                    </View>

                    {[
                        { key: "total", label: "Total Reports", isBold: true },
                        { key: "with_violation", label: "With Violation", isBold: false },
                        { key: "without_violation", label: "Without Violation", isBold: false },
                        { key: "movie", label: "Movie Reports", isBold: false },
                        { key: "tv", label: "TV Reports", isBold: false },
                        { key: "puv", label: "PUV Reports", isBold: false },
                        { key: "video-pm", label: "Video PMs", isBold: false },
                        { key: "non-video-pm", label: "Non-Video PMs", isBold: false },
                        { key: "others", label: "Others", isBold: false },
                    ].map((item, index) => (
                        <View style={index % 2 === 0 ? styles.tableRow : styles.tableRowEven} key={index}>
                            <View style={styles.tableCol}>
                                <Text style={item.isBold ? styles.tableCellBold : styles.tableCell}>{item.label}</Text>
                            </View>
                            <View style={styles.tableCol}>
                                <Text style={item.isBold ? styles.tableCellBold : styles.tableCell}>{summary?.[item.key] ?? 0}</Text>
                            </View>
                        </View>
                    ))}
                </View>

                {/* Trend Table */}
                <Text style={[styles.subTitle, { marginTop: 20, color: "#1e3a8a", fontWeight: "bold" }]}>
                    Monthly Reports Trend
                </Text>
                <View style={styles.table}>
                    <View style={styles.tableRow}>
                        <View style={styles.tableColHeader}>
                            <Text style={styles.tableCellHeader}>Month</Text>
                        </View>
                        <View style={styles.tableColHeader}>
                            <Text style={styles.tableCellHeader}>Total Reports</Text>
                        </View>
                    </View>

                    {trendData?.map((item, index) => (
                        <View style={index % 2 === 0 ? styles.tableRow : styles.tableRowEven} key={`trend-${index}`}>
                            <View style={styles.tableCol}>
                                <Text style={styles.tableCell}>{item.name}</Text>
                            </View>
                            <View style={styles.tableCol}>
                                <Text style={styles.tableCell}>{item.total}</Text>
                            </View>
                        </View>
                    ))}
                </View>

                {/* Footer */}
                <Text style={styles.footer}>
                    © {new Date().getFullYear()} Movie and Television Review and
                    Classification Board (MTRCB)
                </Text>
            </Page>
        </Document>
    );
};
